import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Users, 
  DollarSign, 
  Search, 
  Edit3, 
  Plus, 
  Minus, 
  RefreshCw,
  Shield,
  Eye,
  EyeOff,
  LogOut
} from 'lucide-react';
import supabaseService from '../services/supabaseService';
import pointsService from '../services/pointsService';

const AdminContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  min-height: 100vh;
  background: radial-gradient(900px 600px at 20% -10%, #1b2450 0%, transparent 60%), 
              radial-gradient(900px 600px at 80% 110%, #0f2b5a 0%, transparent 60%), 
              linear-gradient(180deg, #070b16 0%, #0b1020 60%);
  padding: 20px;
  z-index: 1000;
  overflow-y: auto;
`;

const AdminHeader = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px 0;
  border-bottom: 1px solid rgba(255,255,255,0.1);
  margin-bottom: 30px;
`;

const AdminTitle = styled.h1`
  font-size: 32px;
  font-weight: 800;
  background: linear-gradient(135deg, #ff6b6b, #ffa500);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const AdminSubtitle = styled.p`
  color: #a3b0da;
  font-size: 16px;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
  margin-bottom: 30px;
  max-width: 1200px;
  margin-left: auto;
  margin-right: auto;
`;

const StatCard = styled.div`
  background: rgba(255,255,255,0.05);
  border: 1px solid rgba(255,255,255,0.1);
  border-radius: 16px;
  padding: 24px;
  backdrop-filter: blur(20px);
`;

const StatTitle = styled.div`
  color: #a3b0da;
  font-size: 14px;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const StatValue = styled.div`
  color: #e8ecff;
  font-size: 28px;
  font-weight: 700;
`;

const UsersTable = styled.div`
  background: rgba(255,255,255,0.05);
  border: 1px solid rgba(255,255,255,0.1);
  border-radius: 16px;
  backdrop-filter: blur(20px);
  max-width: 1200px;
  margin: 0 auto;
`;

const TableHeader = styled.div`
  padding: 20px 24px;
  border-bottom: 1px solid rgba(255,255,255,0.1);
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const TableTitle = styled.h2`
  color: #e8ecff;
  font-size: 20px;
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const SearchBox = styled.div`
  position: relative;
`;

const SearchInput = styled.input`
  background: rgba(255,255,255,0.1);
  border: 1px solid rgba(255,255,255,0.2);
  border-radius: 8px;
  padding: 8px 12px 8px 36px;
  color: #e8ecff;
  font-size: 14px;
  width: 250px;
  
  &::placeholder {
    color: #a3b0da;
  }
  
  &:focus {
    outline: none;
    border-color: #0A84FF;
  }
`;

const SearchIcon = styled(Search)`
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #a3b0da;
  width: 16px;
  height: 16px;
`;

const TableContent = styled.div`
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableHead = styled.thead`
  background: rgba(255,255,255,0.05);
`;

const TableHeaderCell = styled.th`
  padding: 16px 24px;
  text-align: left;
  color: #e8ecff;
  font-weight: 600;
  font-size: 14px;
  border-bottom: 1px solid rgba(255,255,255,0.1);
`;

const TableBody = styled.tbody``;

const TableRow = styled.tr`
  border-bottom: 1px solid rgba(255,255,255,0.05);
  
  &:hover {
    background: rgba(255,255,255,0.02);
  }
`;

const TableCell = styled.td`
  padding: 16px 24px;
  color: #a3b0da;
  font-size: 14px;
`;

const UserAvatar = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: ${props => props.color || '#0A84FF'};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 600;
  color: white;
`;

const PointsInput = styled.input`
  background: rgba(255,255,255,0.1);
  border: 1px solid rgba(255,255,255,0.2);
  border-radius: 6px;
  padding: 6px 8px;
  color: #e8ecff;
  font-size: 14px;
  width: 80px;
  text-align: center;
  
  &:focus {
    outline: none;
    border-color: #0A84FF;
  }
`;

const ActionButton = styled.button`
  background: ${props => props.variant === 'primary' ? '#0A84FF' : props.variant === 'success' ? '#10B981' : props.variant === 'danger' ? '#ef4444' : 'rgba(255,255,255,0.1)'};
  border: none;
  border-radius: 6px;
  padding: 6px 12px;
  color: white;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  margin: 0 2px;
  
  &:hover {
    opacity: 0.8;
    transform: translateY(-1px);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const AuthModal = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0,0,0,0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const AuthCard = styled.div`
  background: rgba(255,255,255,0.05);
  border: 1px solid rgba(255,255,255,0.1);
  border-radius: 16px;
  padding: 40px;
  backdrop-filter: blur(20px);
  max-width: 400px;
  width: 90%;
`;

const AuthTitle = styled.h2`
  color: #e8ecff;
  font-size: 24px;
  font-weight: 700;
  margin-bottom: 20px;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
`;

const AuthInput = styled.input`
  width: 100%;
  background: rgba(255,255,255,0.1);
  border: 1px solid rgba(255,255,255,0.2);
  border-radius: 8px;
  padding: 12px 16px;
  color: #e8ecff;
  font-size: 16px;
  margin-bottom: 16px;
  
  &::placeholder {
    color: #a3b0da;
  }
  
  &:focus {
    outline: none;
    border-color: #0A84FF;
  }
`;

const AuthButton = styled.button`
  width: 100%;
  background: #0A84FF;
  border: none;
  border-radius: 8px;
  padding: 12px;
  color: white;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: #005a9e;
  }
`;

const ErrorMessage = styled.div`
  color: #ef4444;
  font-size: 14px;
  margin-top: 12px;
  text-align: center;
`;

const LoadingSpinner = styled.div`
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255,255,255,0.3);
  border-radius: 50%;
  border-top-color: #0A84FF;
  animation: spin 1s ease-in-out infinite;
  
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 20px;
  right: 20px;
  background: rgba(255,255,255,0.1);
  border: 1px solid rgba(255,255,255,0.2);
  border-radius: 8px;
  padding: 8px 12px;
  color: #e8ecff;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 8px;
  
  &:hover {
    background: rgba(255,255,255,0.2);
  }
`;

const AdminDashboard = ({ onClose }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authKey, setAuthKey] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  
  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [editingPoints, setEditingPoints] = useState({});
  const [success, setSuccess] = useState('');
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalPoints: 0,
    activeUsers: 0,
    guestUsers: 0
  });

  // 관리자 인증 (환경변수에서 가져오기)
  const ADMIN_KEY = process.env.REACT_APP_ADMIN_KEY;
  
  const handleAuth = async () => {
    if (authKey === ADMIN_KEY) {
      setIsAuthenticated(true);
      setError('');
      await loadUsers();
    } else {
      setError('잘못된 관리자 키입니다.');
    }
  };

  const loadUsers = async () => {
    setLoading(true);
    try {
      console.log('사용자 데이터 로드 시작...');
      
      // Supabase 설정 확인
      if (!supabaseService.isConfigured()) {
        console.error('Supabase가 설정되지 않았습니다.');
        setError('Supabase 설정이 필요합니다.');
        return;
      }
      
      // Supabase에서 사용자 데이터 가져오기
      const response = await supabaseService.listUsers();
      
      console.log('Supabase 응답:', response);
      
      // Supabase REST API는 직접 배열을 반환하거나 {data: [...]} 형태로 반환
      const userData = Array.isArray(response) ? response : (response.data || response);
      
      if (userData && userData.length > 0) {
        console.log('사용자 데이터:', userData);
        setUsers(userData);
        calculateStats(userData);
      } else {
        console.log('사용자 데이터가 없습니다.');
        setUsers([]);
        calculateStats([]);
      }
    } catch (error) {
      console.error('사용자 데이터 로드 실패:', error);
      setError('사용자 데이터를 불러올 수 없습니다: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (userData) => {
    const totalUsers = userData.length;
    const totalPoints = userData.reduce((sum, user) => sum + (user.points || 0), 0);
    const activeUsers = userData.filter(user => user.points > 0).length;
    const guestUsers = userData.filter(user => !user.email).length;
    
    setStats({ totalUsers, totalPoints, activeUsers, guestUsers });
  };

  const handlePointsChange = (userId, newPoints) => {
    setEditingPoints(prev => ({
      ...prev,
      [userId]: newPoints
    }));
  };

  const savePoints = async (userId, newPoints) => {
    try {
      // 사용자 정보 찾기
      const user = users.find(u => u.id === userId);
      console.log('업데이트할 사용자 정보:', user);
      
      let response;
      try {
        // 먼저 userId로 시도
        response = await supabaseService.updateUserPoints(userId, newPoints);
        console.log('userId로 포인트 업데이트 성공:', response);
      } catch (idError) {
        console.log('userId로 업데이트 실패, email로 시도:', idError);
        // userId로 실패하면 email로 시도
        if (user && user.email) {
          response = await supabaseService.updateUserPointsByEmail(user.email, newPoints);
          console.log('email로 포인트 업데이트 성공:', response);
        } else {
          throw idError;
        }
      }
      
      console.log('포인트 업데이트 응답:', response);
      console.log('업데이트할 사용자 ID:', userId);
      console.log('업데이트할 포인트:', newPoints);
      
      // 로컬 상태 업데이트
      const updatedUsers = users.map(user => 
        user.id === userId ? { ...user, points: parseInt(newPoints) } : user
      );
      setUsers(updatedUsers);
      
      setEditingPoints(prev => {
        const newState = { ...prev };
        delete newState[userId];
        return newState;
      });
      
      // 현재 로그인한 사용자의 포인트라면 즉시 동기화
      const updatedUser = updatedUsers.find(user => user.id === userId);
      if (updatedUser) {
        const currentAccount = JSON.parse(localStorage.getItem('ai_agent_account') || '{}');
        if (currentAccount.email === updatedUser.email) {
          console.log('현재 로그인 사용자 포인트 동기화:', newPoints);
          
          // localStorage 계정 정보 업데이트
          const updatedAccount = { ...currentAccount, points: parseInt(newPoints) };
          localStorage.setItem('ai_agent_account', JSON.stringify(updatedAccount));
          
          // pointsService도 동기화
          if (typeof pointsService !== 'undefined' && pointsService.setBalance) {
            pointsService.setBalance(parseInt(newPoints));
          }
          
          // 페이지 새로고침으로 UI 업데이트 (실시간 반영)
          window.dispatchEvent(new Event('storage'));
        }
      }
      
      // 통계 업데이트
      await loadUsers();
      
      setSuccess('포인트가 성공적으로 업데이트되었습니다.');
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('포인트 저장 실패:', error);
      setError('포인트 저장에 실패했습니다: ' + error.message);
    }
  };

  const addPoints = async (userId, amount) => {
    const user = users.find(u => u.id === userId);
    const newPoints = (user.points || 0) + amount;
    await savePoints(userId, newPoints);
  };

  const migrateAuthUsers = async () => {
    try {
      console.log('Auth 사용자 마이그레이션 시작...');
      
      // 먼저 기존 사용자 목록을 확인
      const existingUsers = await supabaseService.listUsers();
      const existingEmails = existingUsers.map(user => user.email);
      
      // 실제 Auth 사용자들을 마이그레이션 (여기서는 예시로 실제 이메일 사용)
      const authUsers = [
        {
          email: 'ckdgnssla12@gmail.com', // 실제 Auth에 등록된 이메일
          name: '관리자',
          phone: '010-0000-0000',
          points: 1500
        }
      ];
      
      let migratedCount = 0;
      
      for (const user of authUsers) {
        // 이미 존재하는 사용자는 건너뛰기
        if (existingEmails.includes(user.email)) {
          console.log(`사용자 ${user.email}은 이미 존재합니다. 건너뜁니다.`);
          continue;
        }
        
        try {
          const response = await supabaseService.upsertUser(user);
          console.log(`사용자 ${user.email} 추가 결과:`, response);
          migratedCount++;
        } catch (error) {
          console.error(`사용자 ${user.email} 추가 실패:`, error);
        }
      }
      
      if (migratedCount > 0) {
        setSuccess(`${migratedCount}명의 사용자가 마이그레이션되었습니다.`);
        setError(''); // 에러 메시지 초기화
        await loadUsers(); // 사용자 목록 새로고침
        
        // 3초 후 성공 메시지 자동 제거
        setTimeout(() => {
          setSuccess('');
        }, 3000);
      } else {
        setSuccess('마이그레이션할 새로운 사용자가 없습니다.');
        setTimeout(() => {
          setSuccess('');
        }, 3000);
      }
    } catch (error) {
      console.error('사용자 마이그레이션 실패:', error);
      setError('사용자 마이그레이션 실패: ' + error.message);
    }
  };

  const subtractPoints = async (userId, amount) => {
    const user = users.find(u => u.id === userId);
    const newPoints = Math.max(0, (user.points || 0) - amount);
    await savePoints(userId, newPoints);
  };

  const filteredUsers = users.filter(user => 
    user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (!isAuthenticated) {
    return (
      <AuthModal
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        <AuthCard>
          <AuthTitle>
            <Shield size={24} />
            관리자 인증
          </AuthTitle>
          <AuthInput
            type="password"
            placeholder="관리자 키를 입력하세요"
            value={authKey}
            onChange={(e) => setAuthKey(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleAuth()}
          />
          <AuthButton onClick={handleAuth}>
            {loading ? <LoadingSpinner /> : '인증'}
          </AuthButton>
          {error && <ErrorMessage>{error}</ErrorMessage>}
        </AuthCard>
      </AuthModal>
    );
  }

  return (
    <AdminContainer>
      {onClose && (
        <CloseButton onClick={onClose}>
          ✕ 메인으로 돌아가기
        </CloseButton>
      )}
      <AdminHeader>
        <AdminTitle>
          <Shield size={32} />
          관리자 대시보드
        </AdminTitle>
        <AdminSubtitle>One-Line Paper 사용자 및 포인트 관리</AdminSubtitle>
      </AdminHeader>

      {error && (
        <div style={{
          background: 'rgba(239, 68, 68, 0.1)',
          border: '1px solid rgba(239, 68, 68, 0.3)',
          borderRadius: '8px',
          padding: '16px',
          marginBottom: '20px',
          color: '#ef4444',
          textAlign: 'center'
        }}>
          {error}
        </div>
      )}
      
      {success && (
        <div style={{
          background: 'rgba(34, 197, 94, 0.1)',
          border: '1px solid rgba(34, 197, 94, 0.3)',
          borderRadius: '8px',
          padding: '16px',
          marginBottom: '20px',
          color: '#22c55e',
          textAlign: 'center'
        }}>
          {success}
        </div>
      )}

      <StatsGrid>
        <StatCard>
          <StatTitle>
            <Users size={16} />
            총 사용자
          </StatTitle>
          <StatValue>{stats.totalUsers}</StatValue>
        </StatCard>
        <StatCard>
          <StatTitle>
            <DollarSign size={16} />
            총 포인트
          </StatTitle>
          <StatValue>{stats.totalPoints.toLocaleString()}</StatValue>
        </StatCard>
        <StatCard>
          <StatTitle>
            <Eye size={16} />
            활성 사용자
          </StatTitle>
          <StatValue>{stats.activeUsers}</StatValue>
        </StatCard>
        <StatCard>
          <StatTitle>
            <EyeOff size={16} />
            게스트 사용자
          </StatTitle>
          <StatValue>{stats.guestUsers}</StatValue>
        </StatCard>
      </StatsGrid>

      <div style={{ marginBottom: '30px', textAlign: 'center' }}>
        <button 
          onClick={migrateAuthUsers}
          style={{
            padding: '12px 24px',
            backgroundColor: '#4CAF50',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '500',
            transition: 'all 0.2s ease'
          }}
          onMouseOver={(e) => e.target.style.backgroundColor = '#45a049'}
          onMouseOut={(e) => e.target.style.backgroundColor = '#4CAF50'}
        >
          🔄 Auth 사용자를 Users 테이블로 마이그레이션
        </button>
      </div>

      <UsersTable>
        <TableHeader>
          <TableTitle>
            <Users size={20} />
            사용자 관리
          </TableTitle>
          <SearchBox>
            <SearchIcon />
            <SearchInput
              placeholder="이메일 또는 이름으로 검색..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </SearchBox>
        </TableHeader>
        
        <TableContent>
          <Table>
            <TableHead>
              <tr>
                <TableHeaderCell>사용자</TableHeaderCell>
                <TableHeaderCell>이메일</TableHeaderCell>
                <TableHeaderCell>포인트</TableHeaderCell>
                <TableHeaderCell>가입일</TableHeaderCell>
                <TableHeaderCell>액션</TableHeaderCell>
              </tr>
            </TableHead>
            <TableBody>
              {filteredUsers.map((user) => (
                <TableRow key={user.id}>
                  <TableCell>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <UserAvatar color={user.avatar_color}>
                        {user.avatar_char || user.name?.[0] || '?'}
                      </UserAvatar>
                      <div>
                        <div style={{ color: '#e8ecff', fontWeight: '600' }}>
                          {user.name || '이름 없음'}
                        </div>
                        <div style={{ fontSize: '12px', color: '#a3b0da' }}>
                          {user.phone || '전화번호 없음'}
                        </div>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    {user.email || '게스트 사용자'}
                  </TableCell>
                  <TableCell>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <PointsInput
                        value={editingPoints[user.id] !== undefined ? editingPoints[user.id] : user.points || 0}
                        onChange={(e) => handlePointsChange(user.id, e.target.value)}
                        onBlur={() => savePoints(user.id, editingPoints[user.id] || user.points || 0)}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            savePoints(user.id, editingPoints[user.id] || user.points || 0);
                          }
                        }}
                      />
                      <ActionButton
                        variant="success"
                        onClick={() => addPoints(user.id, 100)}
                        title="100포인트 추가"
                      >
                        <Plus size={12} />
                      </ActionButton>
                      <ActionButton
                        variant="danger"
                        onClick={() => subtractPoints(user.id, 100)}
                        title="100포인트 차감"
                      >
                        <Minus size={12} />
                      </ActionButton>
                    </div>
                  </TableCell>
                  <TableCell>
                    {user.created_at ? new Date(user.created_at).toLocaleDateString('ko-KR') : '-'}
                  </TableCell>
                  <TableCell>
                    <ActionButton
                      variant="primary"
                      onClick={() => loadUsers()}
                      title="새로고침"
                    >
                      <RefreshCw size={12} />
                    </ActionButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContent>
      </UsersTable>
    </AdminContainer>
  );
};

export default AdminDashboard;
